import React, { useState, useEffect } from 'react';

const Modal = ({ setIsModalOpen, onCreateItem, isLoading }) => {
    const [formData, setFormData] = useState({
        name: '',
        category: 'Cultural',
        status: 'Planned',
        isChecked: false,
        tripCost: ''
    });
    const [errors, setErrors] = useState({});

    // Close modal with Escape key
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                setIsModalOpen(false);
            }
        };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [setIsModalOpen]);

    const validateForm = () => {
        const newErrors = {};

        if (!formData.name.trim()) {
            newErrors.name = 'Should not be empty';
        }

        if (!formData.tripCost || formData.tripCost <= 0) {
            newErrors.tripCost = 'Trip cost must be a positive number';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = () => {
        if (validateForm()) {
            onCreateItem(formData);
            setFormData({ name: '', category: '', status: 'Planned', isChecked: false, tripCost: '' });
            setErrors({});
        } else {
            // Show native browser alert for validation error
            alert('Please fill in all required fields');
        }
    };

    const handleInputChange = (field, value) => {
        // Convert tripCost to integer
        if (field === 'tripCost') {
            value = value === '' ? '' : parseInt(value) || '';
        }

        setFormData({ ...formData, [field]: value });

        // Clear error when user starts typing
        if (errors[field]) {
            setErrors({ ...errors, [field]: '' });
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 modal-overlay" id="modal-overlay">
            <div className="bg-white rounded-lg p-6 w-96 max-w-lg modal-content" id="modal-content" data-test-id="modal-content">
                <div className="modal-header mb-4">
                    <h3 className="text-xl font-bold modal-title" id="modal-title" data-test-id="modal-title">
                        Add Travel Item
                    </h3>
                </div>

                <div className="modal-body" data-test-id="modal-body">
                    <div className="space-y-4 item-form" id="item-form">
                        {/* Input field with validation */}
                        <div className="form-group">
                            <label className="block text-sm font-medium text-gray-700 label" htmlFor="item-name" data-test-id="name-label">
                                Travel Destination *
                            </label>
                            {errors.name && (
                                <div className="validation-error text-red-600 text-sm mt-1" id="name-error" data-test-id="name-error">
                                    {errors.name}
                                </div>
                            )}
                            <input
                                type="text"
                                id="item-name"
                                className={`mt-1 block w-full border rounded-md px-3 py-2 input-field ${errors.name ? 'border-red-500' : 'border-gray-300'
                                    }`}
                                value={formData.name}
                                onChange={(e) => handleInputChange('name', e.target.value)}
                                placeholder="Enter destination name"
                            />
                        </div>

                        {/* Dropdown */}
                        <div className="form-group" data-test-id="category-group">
                            <label className="block text-sm font-medium text-gray-700 label" htmlFor="item-category">
                                Travel Type
                            </label>
                            <select
                                id="item-category"
                                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 dropdown"
                                data-test-id="category-dropdown"
                                value={formData.category}
                                onChange={(e) => handleInputChange('category', e.target.value)}
                            >
                                <option value="">Select Travel Type</option>
                                <option value="Business">Business</option>
                                <option value="Leisure">Leisure</option>
                                <option value="Adventure">Adventure</option>
                                <option value="Cultural">Cultural</option>
                            </select>
                        </div>

                        {/* Trip Cost field */}
                        <div className="form-group">
                            <label className="block text-sm font-medium text-gray-700 label" htmlFor="item-cost" data-test-id="cost-label">
                                Trip Cost ($) *
                            </label>
                            {errors.tripCost && (
                                <div className="validation-error text-red-600 text-sm mt-1" id="cost-error" data-test-id="cost-error">
                                    {errors.tripCost}
                                </div>
                            )}
                            <input
                                type="number"
                                id="item-cost"
                                className={`mt-1 block w-full border rounded-md px-3 py-2 input-field ${errors.tripCost ? 'border-red-500' : 'border-gray-300'
                                    }`}
                                value={formData.tripCost}
                                onChange={(e) => handleInputChange('tripCost', e.target.value)}
                                placeholder="Enter trip cost"
                                min="1"
                                data-test-id="cost-input"
                            />
                        </div>

                        {/* Status dropdown */}
                        <div className="form-group">
                            <label className="block text-sm font-medium text-gray-700 label" htmlFor="item-status" data-test-id="status-label">
                                Trip Status
                            </label>
                            <select
                                id="item-status"
                                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 dropdown"
                                value={formData.status}
                                onChange={(e) => handleInputChange('status', e.target.value)}
                            >
                                <option value="Planned">Planned</option>
                                <option value="Completed">Completed</option>
                                <option value="Cancelled">Cancelled</option>
                            </select>
                        </div>

                        {/* Checkbox */}
                        <div className="form-group checkbox-group" data-test-id="checkbox-group">
                            <label className="flex items-center checkbox-label">
                                <input
                                    type="checkbox"
                                    id="item-checked"
                                    className="mr-2 checkbox-input"
                                    checked={formData.isChecked}
                                    onChange={(e) => handleInputChange('isChecked', e.target.checked)}
                                />
                                <span className="checkbox-text">Add to favorites</span>
                            </label>
                        </div>
                    </div>
                </div>

                <div className="modal-footer flex justify-end space-x-2 mt-6">
                    <button
                        type="button"
                        className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 btn-cancel"
                        id="cancel-btn"
                        data-test-id="cancel-button"
                        onClick={() => setIsModalOpen(false)}
                        disabled={isLoading}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className={`px-4 py-2 rounded btn-submit flex items-center justify-center min-w-20 ${isLoading
                            ? 'bg-blue-400 cursor-not-allowed'
                            : 'bg-blue-500 hover:bg-blue-600'
                            } text-white`}
                        id="submit-btn"
                        onClick={handleSubmit}
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <>
                                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Saving...
                            </>
                        ) : (
                            'Save'
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default Modal;